import React, { useState, useCallback } from 'react';
import { Card, Button, Badge, Accordion, Spinner, Alert, Modal } from 'react-bootstrap';
import {
  FiFileText,
  FiImage,
  FiExternalLink,
  FiChevronDown,
  FiChevronUp,
  FiEye,
} from 'react-icons/fi';
import { GabStudy, GabStudiesData, GabPageRef, StudyValue } from '../../types';
import { interpretationsApi } from '../../services/api';
import PdfViewer from './PdfViewer';

const FLAG_COLORS: Record<string, string> = { LOW: 'info', HIGH: 'danger', NORMAL: 'success' };

const StudyValuesTable: React.FC<{ values: StudyValue[] }> = ({ values }) => (
  <table className="table table-sm table-bordered mb-0 small">
    <tbody>
      {values.map((v, i) => (
        <tr key={i}>
          <th className="text-muted fw-medium" style={{ width: '40%' }}>{v.name}</th>
          <td>
            {v.value}{v.unit ? ` ${v.unit}` : ''}
            {(v.refLow || v.refHigh) && (
              <span className="text-muted ms-1">({v.refLow}–{v.refHigh})</span>
            )}
            {v.flag && v.flag !== 'NORMAL' && (
              <Badge bg={FLAG_COLORS[v.flag] || 'secondary'} className="ms-1" pill>{v.flag}</Badge>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const StudyVisionDataFallback: React.FC<{ studyType: string; visionData: Record<string, string | null> }> = ({ studyType, visionData }) => {
  if (studyType === 'DENSITOMETRIA') {
    return (
      <>
        {visionData['result'] && <p className="mb-1 small"><strong>Conclusión:</strong> {visionData['result']}</p>}
        <table className="table table-sm table-bordered mb-0 small">
          <tbody>
            {[['T-score', 't_score'], ['Z-score', 'z_score'], ['BQI', 'bqi'], ['SOS', 'sos'], ['BUA', 'bua']]
              .filter(([, key]) => visionData[key] != null)
              .map(([label, key]) => (
                <tr key={key}><th style={{ width: '40%' }}>{label}</th><td>{visionData[key]}</td></tr>
              ))}
          </tbody>
        </table>
      </>
    );
  }
  if (studyType === 'GLUCOSA_CAPILAR') {
    return (
      <div className="d-flex align-items-center gap-2">
        <span className="small">{visionData['value']} {visionData['unit']}</span>
        <Badge bg="secondary">CAPILAR_PUNCION_DIGITAL</Badge>
      </div>
    );
  }
  if (studyType === 'HPYLORI') {
    return (
      <div className="d-flex align-items-center gap-2">
        <small className="text-muted">Resultado:</small>
        <Badge bg={visionData['result'] === 'Negativo' ? 'success' : 'danger'}>{visionData['result']}</Badge>
      </div>
    );
  }
  return null;
};

const PAGE_ROLE_LABELS: Record<string, string> = {
  REPORT: 'Informe',
  TRACING: 'Trazo ECG',
  IMAGE_SHEET: 'Lámina',
};

// The inline column is narrow; a tracing or an audiogram is unreadable there. Every page
// opens full-size in a modal (the physician reads the audiogram here — nobody else does).
const GabPagesViewer: React.FC<{ pages: GabPageRef[]; studyName?: string }> = ({ pages, studyName }) => {
  const [open, setOpen] = useState<GabPageRef | null>(null);
  const label = (page: GabPageRef) => `${PAGE_ROLE_LABELS[page.role] || page.role} (p. ${page.page})`;
  return (
    <div className="mt-2">
      <small className="text-muted d-block mb-1">Imágenes del estudio (clic para ampliar):</small>
      {pages.map((page) => (
        <div key={page.page} className="mb-2">
          <small className="text-muted d-block mb-1">{label(page)}</small>
          {page.url ? (
            <img
              src={page.url}
              alt={`${studyName || ''} ${label(page)}`.trim()}
              style={{ maxWidth: '100%', cursor: 'zoom-in' }}
              className="border rounded"
              role="button"
              onClick={() => setOpen(page)}
            />
          ) : (
            <span className="text-muted small fst-italic">Imagen no disponible</span>
          )}
        </div>
      ))}
      <Modal show={open !== null} onHide={() => setOpen(null)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            {studyName ? `${studyName} — ` : ''}{open ? label(open) : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center p-2" style={{ maxHeight: '85vh', overflow: 'auto' }}>
          {open?.url && (
            <img
              src={open.url}
              alt={`${studyName || ''} ${label(open)}`.trim()}
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

interface GabStudiesPanelProps {
  interpretationId: string;
  gabStudies?: GabStudiesData;
  gabStudiesList?: GabStudy[];
}

/**
 * GabStudiesPanel displays GAB (Gabinete/Cabinet) studies.
 * Shows imaging studies like radiology, ECG, ultrasound with PDF interpretations and DICOM links.
 */
const GabStudiesPanel: React.FC<GabStudiesPanelProps> = ({
  interpretationId,
  gabStudies,
  gabStudiesList,
}) => {
  const [expandedStudy, setExpandedStudy] = useState<string | null>(null);
  const [pdfViewerState, setPdfViewerState] = useState<{
    show: boolean;
    url: string | null;
    title: string;
    loading: boolean;
    error: string | null;
  }>({
    show: false,
    url: null,
    title: '',
    loading: false,
    error: null,
  });

  // Get studies from either gabStudies.studies or gabStudiesList
  const studies = gabStudies?.studies || gabStudiesList || [];
  const overallSummary = gabStudies?.overall_summary;

  const handleViewPdf = useCallback(async (study: GabStudy) => {
    if (!study.has_interpretation_pdf) return;

    setPdfViewerState({
      show: true,
      url: null,
      title: `${study.study_name} - Interpretación`,
      loading: true,
      error: null,
    });

    try {
      const response = await interpretationsApi.getGabPdfUrl(
        interpretationId,
        study.study_code
      );
      setPdfViewerState(prev => ({
        ...prev,
        url: response.presigned_url,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Error fetching PDF URL:', error);
      setPdfViewerState(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.error || 'No se pudo cargar el PDF',
      }));
    }
  }, [interpretationId]);

  const handleClosePdfViewer = useCallback(() => {
    setPdfViewerState({
      show: false,
      url: null,
      title: '',
      loading: false,
      error: null,
    });
  }, []);

  const handleOpenDicom = useCallback((viewerUrl: string) => {
    window.open(viewerUrl, '_blank');
  }, []);

  const toggleStudy = (studyCode: string) => {
    setExpandedStudy(prev => prev === studyCode ? null : studyCode);
  };

  if (studies.length === 0) {
    return (
      <Card className="gab-studies-panel mb-3">
        <Card.Body className="text-center text-muted py-4">
          <FiImage size={32} className="mb-2 opacity-50" />
          <p className="mb-0">No hay estudios de gabinete disponibles</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="gab-studies-panel mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <FiImage className="text-primary" />
            <span className="fw-semibold">Estudios de Gabinete</span>
            <Badge bg="secondary" pill>
              {studies.length}
            </Badge>
          </div>
        </Card.Header>

        {overallSummary && (
          <Card.Body className="border-bottom bg-light">
            <small className="text-muted d-block mb-1">Resumen General:</small>
            <p className="mb-0 small">{overallSummary}</p>
          </Card.Body>
        )}

        <Card.Body className="p-0">
          <Accordion activeKey={expandedStudy || undefined}>
            {studies.map((study, index) => (
              <Accordion.Item
                key={study.study_code}
                eventKey={study.study_code}
                className="border-0 border-bottom"
              >
                <Accordion.Header
                  onClick={() => toggleStudy(study.study_code)}
                  className="py-2"
                >
                  <div className="d-flex align-items-center justify-content-between w-100 pe-3">
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted small">{index + 1}.</span>
                      <span className="fw-medium">{study.study_name}</span>
                      <span className="text-muted small">({study.study_code})</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {study.has_interpretation_pdf && (
                        <Badge bg="info" className="d-flex align-items-center gap-1">
                          <FiFileText size={12} />
                          PDF
                        </Badge>
                      )}
                      {study.has_dicom_images && (
                        <Badge bg="success" className="d-flex align-items-center gap-1">
                          <FiImage size={12} />
                          DICOM
                        </Badge>
                      )}
                      {study.read_as === 'TEXT_LAYER' && (
                        <Badge bg="secondary">Texto extraído</Badge>
                      )}
                      {study.read_as === 'IMAGE_CROP' && (
                        <Badge bg="warning">Visión IA</Badge>
                      )}
                      {study.read_as === 'DATA' && (
                        <Badge bg="primary">Datos</Badge>
                      )}
                      {study.read_as === 'IMAGE_ONLY' && (
                        <Badge bg="info">Imagen</Badge>
                      )}
                      {(study.read_as === null || study.read_as === undefined) && (
                        <Badge bg="light" className="text-muted">Origen desconocido</Badge>
                      )}
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="bg-light">
                  {/* E12-F01: extraction failed alert */}
                  {study.extraction_status === 'extraction_failed' && (
                    <Alert variant="warning" className="mb-3 py-2">
                      Extracción fallida — revisar estudio manualmente.
                    </Alert>
                  )}

                  {/* E12-F01: EKG — signer card + findings + conclusion + pages */}
                  {study.study_type === 'EKG' && study.extraction_status !== 'extraction_failed' && (
                    <div className="mb-2">
                      {study.signed_by && (
                        <div className="mb-2 p-2 border rounded bg-white">
                          <small className="text-muted d-block mb-1">Firmante:</small>
                          <p className="mb-0 small fw-medium">{study.signed_by.name}</p>
                          <p className="mb-0 small text-muted">
                            {study.signed_by.cedula_especialidad
                              ? `Céd. Prof: ${study.signed_by.cedula} · Céd. Esp: ${study.signed_by.cedula_especialidad}`
                              : `Cédula: ${study.signed_by.cedula}`}{' '}· {study.signed_by.especialidad}
                          </p>
                        </div>
                      )}
                      {study.findings && (
                        <div className="mb-2">
                          <small className="text-muted d-block mb-1">Hallazgos:</small>
                          <p className="mb-0 small">{study.findings}</p>
                        </div>
                      )}
                      {study.conclusion && (
                        <div className="mb-2">
                          <small className="text-muted d-block mb-1">Conclusión:</small>
                          <p className="mb-0 small">{study.conclusion}</p>
                        </div>
                      )}
                      {study.pages && study.pages.length > 0 && (
                        <GabPagesViewer pages={study.pages} studyName={study.study_name} />
                      )}
                    </div>
                  )}

                  {/* E12-F01: USG mamario — signer card + findings + conclusion + BI-RADS + pages */}
                  {study.study_type === 'USG_MAMARIO' && study.extraction_status !== 'extraction_failed' && (
                    <div className="mb-2">
                      {study.signed_by && (
                        <div className="mb-2 p-2 border rounded bg-white">
                          <small className="text-muted d-block mb-1">Firmante:</small>
                          <p className="mb-0 small fw-medium">{study.signed_by.name}</p>
                          <p className="mb-0 small text-muted">
                            {study.signed_by.cedula_especialidad
                              ? `Céd. Prof: ${study.signed_by.cedula} · Céd. Esp: ${study.signed_by.cedula_especialidad}`
                              : `Cédula: ${study.signed_by.cedula}`}{' '}· {study.signed_by.especialidad}
                          </p>
                        </div>
                      )}
                      {study.findings && (
                        <div className="mb-2">
                          <small className="text-muted d-block mb-1">Hallazgos:</small>
                          <p className="mb-0 small">{study.findings}</p>
                        </div>
                      )}
                      {study.conclusion && (
                        <div className="mb-2">
                          <small className="text-muted d-block mb-1">Conclusión:</small>
                          <p className="mb-0 small">{study.conclusion}</p>
                        </div>
                      )}
                      {study.bi_rads && (
                        <div className="mb-2">
                          <small className="text-muted d-block mb-1">BI-RADS:</small>
                          <Badge bg="info">{study.bi_rads}</Badge>
                        </div>
                      )}
                      {study.pages && study.pages.length > 0 && (
                        <GabPagesViewer pages={study.pages} studyName={study.study_name} />
                      )}
                    </div>
                  )}

                  {/* IMAGE_ONLY (audiometría): sheet only — the physician reads it in the modal and
                      writes the reading in the main interpretation editor */}
                  {study.read_as === 'IMAGE_ONLY' && study.extraction_status !== 'extraction_failed' && (
                    <div className="mb-2">
                      <p className="mb-2 small text-muted">
                        Hoja escaneada sin dictamen ni firma. La lectura la escribe el médico revisor en la interpretación.
                      </p>
                      {study.pages && study.pages.length > 0 ? (
                        <GabPagesViewer pages={study.pages} studyName={study.study_name} />
                      ) : (
                        <span className="text-muted small fst-italic">Sin imágenes</span>
                      )}
                    </div>
                  )}

                  {/* v2 DATA: Familia B — structured values table (DENSITOMETRIA, GLUCOSA_CAPILAR, HPYLORI) */}
                  {['DENSITOMETRIA', 'GLUCOSA_CAPILAR', 'HPYLORI'].includes(study.study_type || '') &&
                    study.extraction_status !== 'extraction_failed' && (
                    <div className="mb-2">
                      {study.values && study.values.length > 0 ? (
                        <StudyValuesTable values={study.values} />
                      ) : study.vision_data ? (
                        /* Legacy IMAGE_CROP fallback */
                        <StudyVisionDataFallback studyType={study.study_type!} visionData={study.vision_data} />
                      ) : null}
                    </div>
                  )}

                  {/* Legacy (no study_type): summary and extracted text */}
                  {!study.study_type && (
                    <>
                      {study.summary && (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Resumen:</small>
                          <p className="mb-0 small">{study.summary}</p>
                        </div>
                      )}
                      {study.extracted_text_preview && !study.summary && (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Vista previa del contenido:</small>
                          <p className="mb-0 small text-muted fst-italic">
                            {study.extracted_text_preview}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Action buttons (available for all study types when PDF/DICOM exist) */}
                  <div className="d-flex gap-2 flex-wrap mt-2">
                    {study.has_interpretation_pdf && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewPdf(study)}
                        className="d-flex align-items-center gap-1"
                      >
                        <FiEye size={14} />
                        Ver PDF
                      </Button>
                    )}
                    {study.has_dicom_images && study.dicom_viewer_url && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        onClick={() => handleOpenDicom(study.dicom_viewer_url!)}
                        className="d-flex align-items-center gap-1"
                      >
                        <FiExternalLink size={14} />
                        Abrir DICOM
                      </Button>
                    )}
                  </div>

                  {/* No content message (legacy studies without any data) */}
                  {!study.study_type && !study.summary && !study.extracted_text_preview && !study.has_interpretation_pdf && !study.has_dicom_images && study.extraction_status !== 'extraction_failed' && (
                    <p className="text-muted small mb-0 fst-italic">
                      Sin contenido disponible para este estudio
                    </p>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Card.Body>
      </Card>

      {/* PDF Viewer Modal */}
      <PdfViewer
        url={pdfViewerState.url}
        title={pdfViewerState.title}
        show={pdfViewerState.show}
        onHide={handleClosePdfViewer}
        loading={pdfViewerState.loading}
        error={pdfViewerState.error}
      />
    </>
  );
};

export default GabStudiesPanel;
